import { Checkbox, Grid, Popover, Typography } from 'gu-ui-kit'
import React from 'react'
import EditableField from 'components/EditableField'
import styles from 'modules/Services/CampaignsEditor/campaigns-editor.module.scss'
import HeaderWithTooltip from 'modules/Services/CampaignsEditor/subcomponents/HeaderWithTooltip'
import { StatusBadge } from 'modules/Services/CampaignsEditor/subcomponents/StatusBadge'
import InfoPopover from 'modules/Services/CampaignsEditor/subcomponents/InfoPopover'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { numWord } from 'utils/numWord'
import { formatByLocale } from 'utils/formatMoney'

export default (
  { applyName, setColumnVisibility, setRowSelection, setSorting, pagination, summary, ...rest },
  t,
) => {
  const { md } = useBreakpoint()

  return {
    columns: [
      {
        enableHiding: false,
        id: 'name',
        label: t('campaigns_editor::banners.table.head.name.label'),
        accessorKey: 'name',
        header: ({ table }) => (
          <Grid
            gap={24}
            wrap={false}
            alignItems={'center'}
          >
            <Checkbox
              checked={table.getIsAllRowsSelected()}
              onChange={table.getToggleAllRowsSelectedHandler()}
            />
            <HeaderWithTooltip
              title={t('campaigns_editor::banners.table.head.name.label')}
              tooltipText={t('campaigns_editor::banners.table.head.name.tooltip')}
            />
          </Grid>
        ),
        footer: () => (
          <Typography
            variant={'Subtitle-20'}
            color={'Label-Fifth'}
            noWrap
          >
            {t('campaigns_editor::banners.table.foot.total', {
              count: pagination?.total ?? 0,
              banners: numWord(pagination?.total ?? 0, [
                t('campaigns_editor::banners.table.foot.banners_1'),
                t('campaigns_editor::banners.table.foot.banners_2'),
                t('campaigns_editor::banners.table.foot.banners_5'),
              ]),
            })}
          </Typography>
        ),
        cell: ({ row }) => {
          const {
            original: { name, external_id, status },
          } = row

          const events = !md
            ? { triggerOpenEventName: 'onClick' }
            : {
                triggerOpenEventName: 'onMouseEnter',
                triggerCloseEventName: 'onMouseLeave',
              }

          //eslint-disable-next-line
          const { bindPopover, usePopupState, bindTrigger } = Popover.usePopover(events)

          //eslint-disable-next-line
          const popoverState = usePopupState({
            variant: 'popover',
            id: 'info-popover' + external_id,
          })

          return (
            <Grid
              gap={24}
              wrap={false}
              alignItems={'center'}
            >
              <Checkbox
                checked={row.getIsSelected()}
                onChangeHandle={row.getToggleSelectedHandler()}
              />
              <Grid
                direction={'column'}
                gap={8}
                alignItems="flex-start"
              >
                <Grid
                  className={styles['campaigns-editor__edit-name-container']}
                  fluid={false}
                  direction="column"
                  alignItems="flex-start"
                  justifyContent="start"
                  gap={4}
                  {...bindTrigger(popoverState)}
                >
                  <Typography
                    variant={'Body-3'}
                    component={
                      <EditableField
                        disabled={status === 'deleted'}
                        editModeColor={'inherit'}
                        color={'Label-GU-Primary'}
                        id={external_id}
                        name={name}
                        login={name}
                        apply={applyName}
                      >
                        {name}
                      </EditableField>
                    }
                  />
                  <InfoPopover
                    short={true}
                    resource={'banners'}
                    popoverState={popoverState}
                    bindPopover={bindPopover}
                    data={row.original}
                  />
                </Grid>
              </Grid>
            </Grid>
          )
        },
      },
      {
        id: 'agency_client_name',
        label: t('campaigns_editor::banners.table.head.agency_client_name.label'),
        accessorKey: 'agency_client_name',
        header: (
          <HeaderWithTooltip
            title={t('campaigns_editor::banners.table.head.agency_client_name.label')}
            tooltipText={t('campaigns_editor::banners.table.head.agency_client_name.tooltip')}
          />
        ),
        cell: ({
          row: {
            original: { agency_client_name },
          },
        }) => agency_client_name ?? '—',
      },
      {
        id: 'external_id',
        label: t('campaigns_editor::banners.table.head.external_id.label'),
        accessorKey: 'external_id',
        header: (
          <HeaderWithTooltip
            title={t('campaigns_editor::banners.table.head.external_id.label')}
            tooltipText={t('campaigns_editor::banners.table.head.external_id.tooltip')}
          />
        ),
        cell: ({
          row: {
            original: { external_id },
          },
        }) => external_id ?? '—',
      },
      {
        id: 'status',
        label: t('campaigns_editor::banners.table.head.status.label'),
        accessorKey: 'status',
        header: t('campaigns_editor::banners.table.head.status.label'),
        cell: ({
          row: {
            original: { status },
          },
        }) => (
          <>
            <StatusBadge
              status={status}
              statusesMap={{
                active: {
                  icon: 'status active',
                  color: 'Label-Confirm',
                  bgColor: 'Background-Confirm',
                  label: 'campaigns_editor::banners.table.badges.active',
                },
                blocked: {
                  icon: 'status warning',
                  color: 'Label-Error-Primary',
                  bgColor: 'Background-Error',
                  label: 'campaigns_editor::banners.table.badges.blocked',
                },
                deleted: {
                  icon: 'inbox-archive',
                  color: 'Label-Fifth',
                  bgColor: 'Background-Quaternary',
                  label: 'campaigns_editor::banners.table.badges.archived',
                },
              }}
            />
          </>
        ),
      },
      {
        id: 'spent',
        label: t('campaigns_editor::banners.table.head.spent.label'),
        //accessorKey: 'spent',
        header: t('campaigns_editor::banners.table.head.spent.label'),
        footer: () => (
          <Typography
            variant={'Subtitle-20'}
            color={'Label-Fifth'}
            noWrap
          >
            {formatByLocale(summary?.spent) ?? '—'}
          </Typography>
        ),
        cell: ({
          row: {
            original: { spent },
          },
        }) => spent ?? '—',
      },
      {
        id: 'impressions',
        label: t('campaigns_editor::banners.table.head.impressions.label'),
        //accessorKey: 'impressions',
        header: t('campaigns_editor::banners.table.head.impressions.label'),
        footer: () => (
          <Typography
            variant={'Subtitle-20'}
            color={'Label-Fifth'}
            noWrap
          >
            {formatByLocale(summary?.impressions) ?? '—'}
          </Typography>
        ),
        cell: ({
          row: {
            original: { impressions },
          },
        }) => impressions ?? '—',
      },
      {
        id: 'clicks',
        label: t('campaigns_editor::banners.table.head.clicks.label'),
        //accessorKey: 'clicks',
        header: t('campaigns_editor::banners.table.head.clicks.label'),
        footer: () => (
          <Typography
            variant={'Subtitle-20'}
            color={'Label-Fifth'}
            noWrap
          >
            {formatByLocale(summary?.clicks) ?? '—'}
          </Typography>
        ),
        cell: ({
          row: {
            original: { clicks },
          },
        }) => clicks ?? '—',
      },
      {
        id: 'goals',
        label: t('campaigns_editor::banners.table.head.goals.label'),
        //accessorKey: 'goals',
        header: (
          <HeaderWithTooltip
            title={t('campaigns_editor::banners.table.head.goals.label')}
            tooltipText={t('campaigns_editor::banners.table.head.goals.tooltip')}
          />
        ),
        footer: () => (
          <Typography
            variant={'Subtitle-20'}
            color={'Label-Fifth'}
            noWrap
          >
            {formatByLocale(summary?.goals) ?? '—'}
          </Typography>
        ),
        cell: ({
          row: {
            original: { goals },
          },
        }) => goals ?? '—',
      },
      {
        id: 'cr',
        label: t('campaigns_editor::banners.table.head.cr.label'),
        //accessorKey: 'cr',
        header: t('campaigns_editor::banners.table.head.cr.label'),
        footer: () => (
          <Typography
            variant={'Subtitle-20'}
            color={'Label-Fifth'}
            noWrap
          >
            {formatByLocale(summary?.cr) ?? '—'}
          </Typography>
        ),
        cell: ({
          row: {
            original: { cr },
          },
        }) => cr ?? '—',
      },
      {
        id: 'cpa',
        label: t('campaigns_editor::banners.table.head.cpa.label'),
        //accessorKey: 'cpa',
        header: t('campaigns_editor::banners.table.head.cpa.label'),
        footer: () => (
          <Typography
            variant={'Subtitle-20'}
            color={'Label-Fifth'}
            noWrap
          >
            {formatByLocale(summary?.cpa) ?? '—'}
          </Typography>
        ),
        cell: ({
          row: {
            original: { cpa },
          },
        }) => cpa ?? '—',
      },
      {
        id: 'ctr',
        label: t('campaigns_editor::banners.table.head.ctr.label'),
        //accessorKey: 'ctr',
        header: t('campaigns_editor::banners.table.head.ctr.label'),
        footer: () => (
          <Typography
            variant={'Subtitle-20'}
            color={'Label-Fifth'}
            noWrap
          >
            {formatByLocale(summary?.ctr) ?? '—'}
          </Typography>
        ),
        cell: ({
          row: {
            original: { ctr },
          },
        }) => ctr ?? '—',
      },
      {
        id: 'cpc',
        label: t('campaigns_editor::banners.table.head.cpc.label'),
        //accessorKey: 'cpc',
        header: t('campaigns_editor::banners.table.head.cpc.label'),
        footer: () => (
          <Typography
            variant={'Subtitle-20'}
            color={'Label-Fifth'}
            noWrap
          >
            {formatByLocale(summary?.cpc) ?? '—'}
          </Typography>
        ),
        cell: ({
          row: {
            original: { cpc },
          },
        }) => cpc ?? '—',
      },
      {
        id: 'cpm',
        label: t('campaigns_editor::banners.table.head.cpm.label'),
        //accessorKey: 'cpm',
        header: t('campaigns_editor::banners.table.head.cpm.label'),
        footer: () => (
          <Typography
            variant={'Subtitle-20'}
            color={'Label-Fifth'}
            noWrap
          >
            {formatByLocale(summary?.cpa) ?? '—'}
          </Typography>
        ),
        cell: ({
          row: {
            original: { cpm },
          },
        }) => cpm ?? '—',
      },
    ],
    showHeader: true,
    showFooter: true,
    className: styles['campaigns-editor__table'],
    //manualSorting: true,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    getRowId: (row) => row.external_id,
    ...rest,
  }
}
